import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { filter, keys, map } from 'ramda';
import * as AppConstants from '../../constants/AppConstants';
import * as StringConstants from '../../constants/StringConstants';

import s from './SidebarList.css';

import User from '../User/UserContainer';
import {
  formatUnreadMessageCount,
  getChatIcon,
  getChatIdByPathname,
  isDirectMessage,
  isOneToOneDirectMessage,
  isSelfDirectMessage,
} from '../../helpers/ChatUtils';
import { X } from 'react-feather';
import * as routes from '../../constants/UiRoutes';
import { logError } from '../../helpers/BrowserUtils';

/*
 * Helper methods
 */
const hideChatHandler = (e, chatId, hideChat, history) => {
  e.preventDefault();
  e.stopPropagation();
  hideChat([chatId]);
  history.push('/');
};

const chatItemDisplay = (currentChatId, chat, qaId, title, identityId, hideChat, history) => {
  let chatItem;

  if (isOneToOneDirectMessage(chat)) {
    const userId = isSelfDirectMessage(chat) ? chat.users[0] : chat.users.filter(u => u !== identityId)[0];
    chatItem =
      <Fragment>
        <User id={userId} className={s.chatUser} hideAvatar hideMentionName small noPad whiteOfflineStatus statusMarginRight="10px"/>
      </Fragment>;
  } else {
    chatItem = (
      <Fragment>
        <span className={s.itemIndicator} data-qa={`${qaId}-${chat.type}`}>
          {getChatIcon(chat)}
        </span>
        <span className={s.chatName} data-qa={`${qaId}-name`} title={chat.name}>
          {chat.name}
        </span>
      </Fragment>
    );
  }

  const unreadMessageCount = chat.unreadCount;
  const isSelectedChat = currentChatId === parseInt(chat.id, 10);
  return (
    <Fragment>
      {chatItem}
      <div className={s.chatNotificationContainer}>
        {
          (() => {
            if (!isSelectedChat && unreadMessageCount > 0) {
              return (
                <span
                  className={classnames({
                    [s.chatNotification]: true,
                    [s.userMention]: chat.userMentioned,
                  })}>
                  {formatUnreadMessageCount(unreadMessageCount)}
                </span>
              );
            } else if (unreadMessageCount === 0 && isDirectMessage(chat)) {
              return (
                <span
                  className={s.hideButton}
                  onClick={e => hideChatHandler(e, chat.id, hideChat, history)}
                >
                  <X size={16} strokeWidth="3" />
                </span>
              );
            }
          })()
        }
      </div>
    </Fragment>
  );
};


const SidebarList = ({
  chats,
  filterFunc,
  title,
  criteria,
  qaId,
  currentLocation,
  identityId,
  hideChat,
  history,
  isFiltered,
}) => {
  const currentChatId = getChatIdByPathname(currentLocation);

  const renderEmptyState = (type, isFiltered ) => {
    switch (type) {
      case AppConstants.CHANNELS:
        return (
          <div className={s.startNewContainer}>
            <p>{isFiltered ? StringConstants.SIDEBAR_NO_CHANNELS_FILTER : StringConstants.SIDEBAR_NO_CHANNELS}</p>
            <Link className={classnames('btn', 'primary')} to={routes.CREATE_CHANNEL_URL}>
              {StringConstants.CREATE_CHANNEL}
            </Link>
            <Link
              className={classnames('btn', 'secondary')}
              to={{
                pathname: routes.BROWSE,
                state: {
                  initialFilterType: AppConstants.FILTER_CHANNELS,
                },
              }}>
              {StringConstants.BROWSE_PUBLIC_CHANNELS}
            </Link>
          </div>
        );

      case AppConstants.DIRECT_MESSAGES:
        return (
          <div className={s.startNewContainer}>
            <p>{isFiltered ? StringConstants.SIDEBAR_NO_DMS_FILTER : StringConstants.SIDEBAR_NO_DMS}</p>
            <Link className={classnames('btn', 'primary')} to={routes.CREATE_DM_URL}>
              {StringConstants.CREATE_DM}
            </Link>
          </div>
        );
      default:
        logError('Unexpected type in SidebarList: ', type);
        break;
    }
  };

  return (
    <div className={s.sidebarList}>
      {map(id => (
        !chats[id].hidden && currentChatId === parseInt(id, 10) ? (
          <span
            key={id}
            id={id}
            className={classnames(s.chatItem, s.chatItemActive)}
          >
            {chatItemDisplay(currentChatId, chats[id], qaId, title, identityId, hideChat, history)}
          </span>
        ) : !chats[id].hidden && (
          <Link
            key={id}
            id={id}
            className={s.chatItem}
            to={`/chat/${id}`}
            data-qa={qaId + id}
          >
            {chatItemDisplay(currentChatId, chats[id], qaId, title, identityId, hideChat, history)}
          </Link>
        )
      ), keys(filter(filterFunc, chats)))}
      {!keys(filter(chat => criteria(chat) && filterFunc(chat) && !chat.hidden, chats)).length &&
        renderEmptyState(title, isFiltered)
      }
    </div>
  );
};

SidebarList.propTypes = {
  chats: PropTypes.object,
  filterFunc: PropTypes.func,
  title: PropTypes.string,
  criteria: PropTypes.func,
  qaId: PropTypes.string.isRequired,
  currentLocation: PropTypes.string.isRequired,
  identityId: PropTypes.number,
};

export default SidebarList;
